import React, { useEffect, useState } from 'react';
import {
  adminGetEmailTemplates, adminUpdateEmailTemplate, adminTestEmailTemplate,
  adminGetTestRecipients, adminAddTestRecipient, adminRemoveTestRecipient,
} from '../services/api';
import Navbar from '../components/Navbar';
import AdminTabBar from '../components/AdminTabBar';

// Grouped by who receives the email, which is how anyone looking for a template
// actually thinks about it. Mirrors the Emails tab in FOOM.
//
// The template key event_viewer_confirm is a historical name — that email now
// goes to booking managers. Renaming a live key buys nothing, so the label and
// description carry the accurate wording instead.
const TEMPLATE_GROUPS = [
  {
    id: 'performers',
    title: 'Emails to performers',
    keys: ['submission_acknowledgement', 'annual_reminder', 'event_booking_performer', 'event_booking_revised'],
  },
  {
    id: 'managers',
    title: 'Emails to booking managers',
    // booking_manager_invite was live but absent from this page, so the one
    // email nobody could read was the one that had been broken since 13 Aug.
    keys: ['booking_manager_invite', 'distribution_notification', 'event_viewer_confirm'],
  },
  {
    id: 'soundies',
    title: 'Emails to sound engineers',
    keys: ['event_soundie'],
  },
];

const VISIBLE_TEMPLATES = TEMPLATE_GROUPS.flatMap((g) => g.keys);

const TEMPLATE_LABELS = {
  booking_manager_invite:     'Booking Manager Invite',
  submission_acknowledgement: 'Submission Acknowledgement',
  annual_reminder:            'Annual Reminder',
  distribution_notification:  'New Performer Submitted (with PDF)',
  event_booking_performer:    'Event Booking Confirmation (Performer)',
  event_booking_revised:      'Change to Booked Event',
  event_viewer_confirm:       'Event Confirmed',
  event_soundie:              'Event — Sound Engineer Email (with PDF)',
};

const TEMPLATE_DESCRIPTIONS = {
  booking_manager_invite:     'Sent by the Invite button on the Booking Managers list (Distribution & Venues). The invite creates the account, so {{setup_url}} is a set-password link valid for 7 days — it is not a registration form. Variables: {{name}}, {{email}}, {{setup_url}}',
  submission_acknowledgement: 'Sent automatically to the performer when they submit their profile. Variables: {{first_name}}',
  annual_reminder:            'Sent annually to performers whose profile is over 1 year old. Variables: {{first_name}}, {{login_url}}',
  distribution_notification:  'Sent to booking managers and admins when a new profile is submitted. The profile PDF and promo photo are attached. Variables: {{stage_name}}, {{performance_type}}, {{genres}}',
  event_booking_performer:    'Sent to each registered performer when an event is confirmed. Variables: {{first_name}}, {{event_name}}, {{event_date}}, {{venue_name}}, {{venue_address}}, {{performer_arrival_time}}, {{show_start_time}}, {{arrival_time}}, {{spot_time}}, {{set_number}}, {{set_name}}, {{slot_number}}, {{lyricist}}, {{composer}}',
  event_booking_revised:      'Sent to performers when a confirmed event is revised. Supersedes the original confirmation. Variables: {{first_name}}, {{event_name}}, {{event_date}}, {{venue_name}}, {{venue_address}}, {{performer_arrival_time}}, {{show_start_time}}, {{arrival_time}}, {{spot_time}}, {{set_number}}, {{set_name}}, {{slot_number}}, {{lyricist}}, {{composer}}',
  event_viewer_confirm:       'Sent to booking managers when an event is confirmed or re-confirmed. Any "additional notes for this event" are appended to the end. Variables: {{event_name}}, {{event_date}}, {{venue_name}}, {{performer_list}}',
  event_soundie:              'Sent to sound engineers when an event is confirmed. The consolidated stage requirements PDF is attached. Variables: {{event_name}}, {{event_date}}, {{venue_name}}',
};

/** Collapsible black header bar for a group of templates. */
function EmailGroup({ title, count, open, onToggle, children }) {
  return (
    <div style={{ marginBottom: open ? '2rem' : '0.6rem' }}>
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', userSelect: 'none',
          background: '#000', color: '#fff', padding: '0.6rem 1rem', borderRadius: '6px',
          marginBottom: open ? '1.25rem' : 0,
        }}
      >
        <span style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block' }}>▶</span>
        <span style={{ fontSize: '1.02rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {title}
        </span>
        <span style={{ fontSize: '0.8rem', opacity: 0.7, marginLeft: 'auto' }}>
          {count} email{count === 1 ? '' : 's'}
        </span>
      </div>
      {open && children}
    </div>
  );
}

/**
 * The addresses every Test button on this page sends to. Modelled on the FOOM
 * panel of the same name, and sitting above the templates for the same reason:
 * a Test button is useless until this list has something in it, so it should be
 * the first thing you see.
 */
function TestRecipientsPanel({ recipients, onAdd, onRemove, adding, removing, error }) {
  const [email, setEmail] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    onAdd(trimmed, () => setEmail(''));
  };

  return (
    <div style={{
      background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px',
      padding: '1rem 1.25rem', marginBottom: '1.75rem',
    }}>
      <div style={{
        fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: '#92400e', marginBottom: '0.4rem',
      }}>
        Test Email Recipients
      </div>
      <p style={{ fontSize: '0.85rem', color: '#78350f', margin: '0 0 0.85rem' }}>
        These addresses receive every <strong>Test</strong> send on this page. A test has no
        real performer or event behind it, so placeholders such as <code>{'{{first_name}}'}</code> stay
        visible in the message rather than being filled with invented data.
      </p>

      {error && <div className="a-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      <form onSubmit={submit} style={{ display: 'flex', gap: '0.5rem', marginBottom: recipients.length ? '0.85rem' : 0 }}>
        <input
          className="a-input"
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ maxWidth: '320px' }}
        />
        <button type="submit" className="a-btn a-btn--navy a-btn--sm" disabled={adding || !email.trim()}>
          {adding ? 'Adding…' : '+ Add'}
        </button>
      </form>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {recipients.map((r) => (
          <span
            key={r.id}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: '#fff', border: '1px solid #fde68a', borderRadius: '999px',
              padding: '0.3rem 0.75rem', fontSize: '0.85rem', color: '#374151',
            }}
          >
            {r.email}
            <button
              onClick={() => onRemove(r)}
              disabled={removing === r.id}
              title={`Remove ${r.email}`}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                color: 'var(--red)', fontSize: '1rem', lineHeight: 1,
              }}
            >
              {removing === r.id ? '…' : '×'}
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AdminEmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  // edits: { [key]: { subject, body, saving, msg } }
  const [edits, setEdits]         = useState({});
  const [openGroups, setOpenGroups] = useState(
    () => Object.fromEntries(TEMPLATE_GROUPS.map((g) => [g.id, true]))
  );

  // ── Test recipients ──
  const [recipients,    setRecipients]    = useState([]);
  const [rcptError,     setRcptError]     = useState('');
  const [addingRcpt,    setAddingRcpt]    = useState(false);
  const [removingRcpt,  setRemovingRcpt]  = useState(null);

  const toggleGroup = (id) => setOpenGroups((o) => ({ ...o, [id]: !o[id] }));

  useEffect(() => {
    adminGetTestRecipients()
      .then((res) => setRecipients(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRcptError('Failed to load the test recipients list.'));
  }, []);

  const handleAddRecipient = async (email, clear) => {
    setAddingRcpt(true);
    setRcptError('');
    try {
      const res = await adminAddTestRecipient({ email });
      setRecipients((list) => [...list, res.data].sort((a, b) => a.email.toLowerCase().localeCompare(b.email.toLowerCase())));
      clear();
    } catch (err) {
      setRcptError(err?.response?.data?.error || 'Failed to add that address.');
    } finally {
      setAddingRcpt(false);
    }
  };

  const handleRemoveRecipient = async (row) => {
    setRemovingRcpt(row.id);
    setRcptError('');
    try {
      await adminRemoveTestRecipient(row.id);
      setRecipients((list) => list.filter((r) => r.id !== row.id));
    } catch (err) {
      setRcptError(err?.response?.data?.error || 'Failed to remove that address.');
    } finally {
      setRemovingRcpt(null);
    }
  };

  useEffect(() => {
    adminGetEmailTemplates()
      .then(res => {
        const data = res.data;
        if (!Array.isArray(data)) {
          console.error('Email templates: unexpected response shape', data);
          setError('Failed to load email templates (unexpected server response).');
          return;
        }
        setTemplates(data);
        const initial = {};
        for (const t of data) {
          initial[t.template_key] = { subject: t.subject, body: t.body, saving: false, msg: '' };
        }
        setEdits(initial);
      })
      .catch(err => {
        console.error('Email templates fetch error:', err?.response?.status, err?.response?.data || err?.message);
        setError('Failed to load email templates.');
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (key) => {
    const edit = edits[key];
    if (!edit) return;
    setEdits(e => ({ ...e, [key]: { ...e[key], saving: true, msg: '' } }));
    try {
      await adminUpdateEmailTemplate(key, { subject: edit.subject, body: edit.body });
      setEdits(e => ({ ...e, [key]: { ...e[key], saving: false, msg: 'Saved.' } }));
    } catch {
      setEdits(e => ({ ...e, [key]: { ...e[key], saving: false, msg: 'Save failed.' } }));
    }
  };

  // The server sends the SAVED template, not what is in the textarea — a test of
  // unsaved text would pass while the real email still went out with the old
  // wording. Unsaved edits are refused here rather than silently ignored.
  const handleTest = async (key) => {
    const edit = edits[key] || {};
    const saved = byKey[key];
    if (saved && (edit.subject !== saved.subject || edit.body !== saved.body)) {
      setEdits(e => ({ ...e, [key]: { ...e[key], msg: 'Save your changes first — a test sends the saved version.' } }));
      return;
    }

    setEdits(e => ({ ...e, [key]: { ...e[key], testing: true, msg: '' } }));
    try {
      const res = await adminTestEmailTemplate(key);
      const sent = res.data?.sent || [];
      const msg = res.data?.error
        ? res.data.error
        : `Test sent to ${sent.length === 1 ? sent[0] : `${sent.length} recipients`}.`;
      setEdits(e => ({ ...e, [key]: { ...e[key], testing: false, msg } }));
    } catch (err) {
      setEdits(e => ({
        ...e,
        [key]: { ...e[key], testing: false, msg: err?.response?.data?.error || 'Test send failed.' },
      }));
    }
  };

  const byKey = Object.fromEntries(
    templates.filter(t => VISIBLE_TEMPLATES.includes(t.template_key)).map(t => [t.template_key, t])
  );

  const renderTemplate = (t) => {
    const edit = edits[t.template_key] || {};
    const isSaving  = edit.saving;
    const isTesting = edit.testing;
    // "Save your changes first" and "Sent, but delivery was refused" are neither
    // successes nor failures of the thing you pressed, so keying the colour off
    // the word "failed" alone would show them in green.
    const isProblem = /failed|refused|first|no test email recipients/i.test(edit.msg || '');
    return (
      <div key={t.template_key} className="a-card" style={{ padding: '1.25rem', marginBottom: '1.25rem' }}>
        <div style={{ marginBottom: '0.9rem' }}>
          <div style={{
            fontSize: '1.05rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em',
            color: '#000', marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb',
          }}>
            {TEMPLATE_LABELS[t.template_key] || t.template_key}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
            {TEMPLATE_DESCRIPTIONS[t.template_key] || ''}
          </div>
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <label className="a-label">Subject</label>
          <input
            className="a-input"
            value={edit.subject || ''}
            onChange={e => setEdits(ed => ({ ...ed, [t.template_key]: { ...ed[t.template_key], subject: e.target.value, msg: '' } }))}
          />
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <label className="a-label">Body</label>
          <textarea
            className="a-input"
            value={edit.body || ''}
            onChange={e => setEdits(ed => ({ ...ed, [t.template_key]: { ...ed[t.template_key], body: e.target.value, msg: '' } }))}
            rows={10}
            style={{ fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            className="a-btn a-btn--navy a-btn--sm"
            onClick={() => handleSave(t.template_key)}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          <button
            className="a-btn a-btn--ghost a-btn--sm"
            onClick={() => handleTest(t.template_key)}
            disabled={isSaving || isTesting || recipients.length === 0}
            title={recipients.length === 0
              ? 'Add a test email recipient at the top of the page first'
              : `Send this email to ${recipients.map((r) => r.email).join(', ')}`}
          >
            {isTesting ? 'Sending…' : 'Test'}
          </button>
          {edit.msg && (
            <span style={{ fontSize: '0.85rem', color: isProblem ? 'var(--red)' : 'var(--green)' }}>
              {edit.msg}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <AdminTabBar />
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--navy)', marginBottom: '0.4rem' }}>
          Email
        </h1>
        <p style={{ fontSize: '0.875rem', color: 'var(--muted)', margin: '0 0 1.5rem' }}>
          The emails the system sends automatically, grouped by who receives them. Click a
          heading to collapse it, or <strong>Test</strong> to send one to yourself first.
        </p>

        <TestRecipientsPanel
          recipients={recipients}
          onAdd={handleAddRecipient}
          onRemove={handleRemoveRecipient}
          adding={addingRcpt}
          removing={removingRcpt}
          error={rcptError}
        />

        {error && <div className="a-error" style={{ marginBottom: '1rem' }}>{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '3rem' }}>Loading…</div>
        ) : templates.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>No templates found.</div>
        ) : (
          TEMPLATE_GROUPS.map((g) => {
            const present = g.keys.map((k) => byKey[k]).filter(Boolean);
            if (present.length === 0) return null;
            return (
              <EmailGroup
                key={g.id}
                title={g.title}
                count={present.length}
                open={openGroups[g.id]}
                onToggle={() => toggleGroup(g.id)}
              >
                {present.map(renderTemplate)}
              </EmailGroup>
            );
          })
        )}
      </div>
    </>
  );
}
